import React, { useEffect, useMemo, useRef } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import type { GridItemKeySelector } from 'react-window';
import { FixedSizeGrid as Grid } from 'react-window';
import { AnnotationCell } from './annotation-cell';
import { FRAME_HEIGHT, FRAME_WIDTH } from './constants';
import type { OnAnnotationCellClick, OnScrollT } from './types';
type PropsT = {
  numCols: number;
  annotationObjectIds: string[];
  onScroll: OnScrollT;
  onCellClick: OnAnnotationCellClick;
  gridContainer: HTMLDivElement | null;
  setGridContainer: (el: HTMLDivElement | null) => void;
};
type CellDataT = {
  annotationObjectIds: string[];
  onCellClick: OnAnnotationCellClick;
};
function useHandleOnScroll(gridContainer: HTMLDivElement | null, onScroll: OnScrollT) {
  const onScrollRef = useRef(onScroll);
  // eslint-disable-next-line react-hooks/refs
  onScrollRef.current = onScroll;
  useEffect(() => {
    if (!gridContainer) {
      return;
    }
    function handleScroll(event: Event) {
      if (!(event.currentTarget instanceof HTMLElement)) {
        return;
      }
      onScrollRef.current({
        scrollLeft: event.currentTarget.scrollLeft,
        scrollTop: event.currentTarget.scrollTop
      });
    }
    gridContainer.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      gridContainer.removeEventListener('scroll', handleScroll);
    };
  }, [gridContainer]);
}
export function AnnotationBodyGrid({
  numCols,
  annotationObjectIds,
  onScroll,
  onCellClick,
  gridContainer,
  setGridContainer
}: PropsT) {
  const numRows = annotationObjectIds.length;
  useHandleOnScroll(gridContainer, onScroll);
  const cellData = useMemo<CellDataT>(() => {
    return {
      annotationObjectIds,
      onCellClick
    };
  }, [annotationObjectIds, onCellClick]);
  const getItemKey: GridItemKeySelector<CellDataT> = ({
    columnIndex,
    rowIndex
  }) => {
    const annotationId = cellData.annotationObjectIds[rowIndex];
    return `${annotationId}-${columnIndex}`;
  };
  return <AutoSizer>
      {({
      width,
      height
    }) => <Grid<CellDataT> outerRef={setGridContainer} itemData={cellData} height={height} width={width} rowCount={numRows} columnCount={numCols} itemKey={getItemKey} columnWidth={FRAME_WIDTH} rowHeight={FRAME_HEIGHT}>
          {AnnotationCell}
        </Grid>}
    </AutoSizer>;
}